'use client';

import type { MouseEvent } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { dataLayerPush, getBaseUrlJoined, layoutEvents, trackCheckoutSignUpImpression } from '@libs/analytics';
import { AccountDrawerType, clsx, usePageLayout } from '@libs/common-next';
import { NAVIGATION_URLS } from '@libs/core';
import { Button, Dropdown, Image, LogoTreatsRewards, TextLink } from '@petsmart-ui/sparky';

import './SignIn.scss';
export const trackingData = (data: object) => Object.assign(data, { ...layoutEvents.headerClick });

export function SignIn() {
    const [isCartPage, setIsCartPage] = useState(false);
    const { flags, openAccountDrawer } = usePageLayout();

    const currentPage = IS_SSR ? '' : new URL(window.location.href).pathname;

    useEffect(() => {
        setIsCartPage(['/checkout-login', '/cart'].includes(currentPage));
    }, [currentPage]);

    const redirectURL = isCartPage
        ? `${NAVIGATION_URLS.velocity.createAccount}?redirectUrl=${currentPage}`
        : NAVIGATION_URLS.velocity.createAccount;

    const wrapperClasses = useMemo(
        () => clsx('legacy-signin', flags.loyaltyAccountMenu ? 'loyalty-account-menu-enabled' : ''),
        [flags.loyaltyAccountMenu]
    );

    // For the main SignIn
    const handleSignInLinkClick = useCallback((e: MouseEvent<HTMLAnchorElement>) => {
        // Prevent default action of the link before data pushed into dataLayer
        e.preventDefault();

        dataLayerPush(
            trackingData({
                selection: 'login',
                link_url: getBaseUrlJoined(NAVIGATION_URLS.legacy.account.base),
            })
        );

        // Go to the link after data pushed into dataLayer
        window.location.assign(NAVIGATION_URLS.velocity.signIn);
    }, []);

    // For the SignIn button in the dropdown
    const handleSignInButtonClick = useCallback(() => {
        trackCheckoutSignUpImpression();
        openAccountDrawer(AccountDrawerType.SIGN_IN);
    }, [openAccountDrawer]);

    return (
        <div className={wrapperClasses} data-testid="sign-in">
            <div className="legacy-signin__button">
                <div className="legacy-signin__copy">
                    <TextLink
                        href={NAVIGATION_URLS.velocity.signIn}
                        onClick={handleSignInLinkClick}
                        className="legacy-signin__link"
                    >
                        <span>Sign In</span>
                        <span className="legacy-account__caret" />
                    </TextLink>
                </div>
            </div>

            <div className="legacy-signin__dropdown-wrapper">
                <Dropdown className="legacy-signin__dropdown">
                    <section className="signin-dropcontent">
                        <div className="signin-dropcontent__container">
                            {flags.loyaltyAccountMenu ? (
                                <LogoTreatsRewards />
                            ) : (
                                <Image
                                    src="https://s7d2.scene7.com/is/image/PetSmart/LY_2_SignInAccountHover_treatslogo_allsize_0611?UT0501L$"
                                    alt="Loyalty Logo"
                                    className="signin-dropcontent__logo"
                                    loading="lazy"
                                    width={130}
                                    height={26}
                                />
                            )}

                            <div className="signin-dropcontent__content">
                                <p>
                                    Join our
                                    <TextLink className="signin-dropcontent__loyalty" href="/treats-rewards">
                                        loyalty program
                                    </TextLink>
                                    &amp; earn points every time you shop!
                                </p>
                                <Button
                                    type="button"
                                    className="signin-dropcontent__button"
                                    variant="primary"
                                    fullWidth
                                    text={flags.loyaltyAccountMenu ? 'Sign in' : 'Login'}
                                    onClick={handleSignInButtonClick}
                                />
                                <TextLink className="signin-dropcontent__textlink" href={redirectURL}>
                                    {flags.loyaltyAccountMenu ? 'Create account' : 'Sign up'}
                                </TextLink>
                            </div>
                        </div>
                    </section>
                </Dropdown>
            </div>

            <div className="legacy-signin__accent" />
        </div>
    );
}
